import { api } from "@/api/client"
import { ColorRank } from "../types/battle"
import {
  DailyTournamentCurrent,
  DailyTournamentDetail,
  DailyTournamentHistoryItem,
} from "../types/dailyVirtualTournament"
import { GameCard } from "../types/battle"
import { guardiansLogApiError } from "../utils/guardiansDebugLog"
import { getGameApiErrorMessage } from "./gameApiErrors"
import { normalizeGameCards } from "../utils/gameCards"
import {
  normalizeDailyTournamentCurrent,
  normalizeDailyTournamentHistoryItem,
} from "../utils/dailyVirtualTournamentNormalize"
import { normalizeVirtualTournamentDetail } from "../utils/virtualTournamentNormalize"

function unwrapGamePayload(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as Record<string, unknown>
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }
  return obj
}

export const getDailyTournamentCurrent =
  async (): Promise<DailyTournamentCurrent | null> => {
    try {
      const { data } = await api.get<unknown>(
        "/game/virtual-tournament-daily/current"
      )
      return normalizeDailyTournamentCurrent(unwrapGamePayload(data))
    } catch (error) {
      guardiansLogApiError("GET /game/virtual-tournament-daily/current", error)
      throw error
    }
  }

export const getDailyTournamentCards = async (
  colorRank: ColorRank
): Promise<GameCard[]> => {
  try {
    const { data } = await api.get<unknown>(
      "/game/virtual-tournament-daily/cards",
      { params: { colorRank, color_rank: colorRank } }
    )
    return normalizeGameCards(data)
  } catch (error) {
    guardiansLogApiError("GET /game/virtual-tournament-daily/cards", error)
    throw error
  }
}

export const registerDailyTournament = async (payload: {
  colorRank: ColorRank
  cardId: string
}): Promise<void> => {
  try {
    await api.post<unknown>("/game/virtual-tournament-daily/register", {
      colorRank: payload.colorRank,
      color_rank: payload.colorRank,
      cardId: payload.cardId,
      card_id: payload.cardId,
    })
  } catch (error) {
    guardiansLogApiError("POST /game/virtual-tournament-daily/register", error)
    throw new Error(
      getGameApiErrorMessage(error, "Не удалось зарегистрироваться на турнир")
    )
  }
}

export const cancelDailyTournament = async (payload: {
  colorRank: ColorRank
}): Promise<void> => {
  try {
    await api.post<unknown>("/game/virtual-tournament-daily/cancel", {
      colorRank: payload.colorRank,
      color_rank: payload.colorRank,
    })
  } catch (error) {
    guardiansLogApiError("POST /game/virtual-tournament-daily/cancel", error)
    throw new Error(
      getGameApiErrorMessage(error, "Не удалось отменить участие")
    )
  }
}

export const getDailyTournamentDetail = async (
  tournamentId: string
): Promise<DailyTournamentDetail | null> => {
  try {
    const { data } = await api.get<unknown>(
      `/game/virtual-tournament-daily/${tournamentId}`
    )
    return normalizeVirtualTournamentDetail(unwrapGamePayload(data))
  } catch (error) {
    guardiansLogApiError(
      `GET /game/virtual-tournament-daily/${tournamentId}`,
      error
    )
    throw error
  }
}

export const getDailyTournamentHistory = async (params?: {
  take?: number
}): Promise<DailyTournamentHistoryItem[]> => {
  try {
    const { data } = await api.get<unknown>(
      "/game/virtual-tournament-daily/history",
      { params: { take: params?.take ?? 20 } }
    )
    const obj = unwrapGamePayload(data)
    const raw = Array.isArray(obj)
      ? obj
      : Array.isArray(obj.items)
        ? obj.items
        : Array.isArray(obj.history)
          ? obj.history
          : []
    return raw
      .map(normalizeDailyTournamentHistoryItem)
      .filter((item): item is DailyTournamentHistoryItem => item != null)
  } catch (error) {
    guardiansLogApiError("GET /game/virtual-tournament-daily/history", error)
    throw error
  }
}
